import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, CircleUser, ClipboardList, FilePlus2, House, ListPlus, Plus, ScanLine } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { hasApprovals } from "@/components/layout/ProtectedLayout";
import { cn } from "@/lib/utils";

const Item = ({ to, icon: Icon, label, badge, end }) => (
  <NavLink to={to} end={end} data-testid={`bottom-nav-${label.toLowerCase()}`}
    className={({ isActive }) => cn("relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold", isActive ? "text-indigo-700" : "text-slate-500")}>
    {({ isActive }) => (<>
      <Icon className="h-[22px] w-[22px]" strokeWidth={isActive ? 2.6 : 2} />{label}
      {badge > 0 && <span className="absolute left-1/2 top-1.5 ml-2 min-w-[16px] rounded-full bg-rose-600 px-1 text-center text-[10px] font-bold leading-4 text-white">{badge > 99 ? "99+" : badge}</span>}
    </>)}
  </NavLink>
);

const Action = ({ icon: Icon, label, sub, tint, onClick, testid }) => (
  <button type="button" onClick={onClick} className="flex min-h-[64px] w-full items-center gap-4 rounded-2xl px-3 text-left hover:bg-slate-50" data-testid={testid}>
    <span className={cn("flex h-12 w-12 items-center justify-center rounded-[16px]", tint)}><Icon className="h-6 w-6" strokeWidth={2.4} /></span>
    <span><span className="block font-semibold text-slate-900">{label}</span><span className="block text-xs text-slate-500">{sub}</span></span>
  </button>
);

/** Mobile bottom navigation for task users, with a raised centre "+" for quick creation. */
export const BottomNav = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const a = user.authority || {};
  const acts = [
    a.create && a.assign && { k: "task", icon: ListPlus, label: "New Task", sub: "Assign a task for endorsement", tint: "bg-indigo-100 text-indigo-700", to: "/tasks" },
    a.submit && { k: "request", icon: FilePlus2, label: "New Request", sub: "Submit a financial request", tint: "bg-teal-100 text-teal-700", to: "/approvals/new" },
    a.submit && { k: "scan", icon: ScanLine, label: "Scan Document", sub: "Attach receipts and quotes", tint: "bg-sky-100 text-sky-700", to: "/approvals/new" },
  ].filter(Boolean);
  const go = (to) => { setOpen(false); navigate(to); };
  useEffect(() => {
    document.documentElement.style.setProperty("--bottom-nav-h", "64px");
    return () => document.documentElement.style.removeProperty("--bottom-nav-h");
  }, []);
  return (
    <>
      <nav className="pb-safe fixed inset-x-0 bottom-0 z-40 flex min-h-[64px] items-end border-t border-slate-100 bg-white shadow-[0_-4px_20px_rgba(15,23,42,0.06)] lg:hidden" data-testid="bottom-nav">
        <Item to="/work" icon={House} label="Home" end />
        <Item to="/tasks" icon={ClipboardList} label="Tasks" />
        {acts.length > 0 && (
          <div className="flex flex-1 justify-center">
            <motion.button type="button" whileTap={{ scale: 0.9 }} onClick={() => setOpen(true)} aria-label="Create"
              className="-mt-6 mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-600/30 ring-4 ring-white" data-testid="bottom-nav-create">
              <Plus className="h-7 w-7" strokeWidth={2.6} />
            </motion.button>
          </div>
        )}
        {hasApprovals(user) && <Item to="/approvals" icon={BadgeCheck} label="Approvals" />}
        <Item to="/profile" icon={CircleUser} label="Profile" />
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="pb-safe rounded-t-[28px]" data-testid="create-sheet">
          <div className="mx-auto mb-2 h-1.5 w-12 rounded-full bg-slate-200" />
          <SheetTitle className="px-3">Create</SheetTitle>
          <div className="mt-2 space-y-1">{acts.map((x) => <Action key={x.k} {...x} onClick={() => go(x.to)} testid={`create-${x.k}`} />)}</div>
        </SheetContent>
      </Sheet>
    </>
  );
};
